import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useWeb3 } from '../contexts/Web3Context';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useWallet } from '@solana/wallet-adapter-react';

const WalletConnectContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  flex-wrap: wrap;
  gap: 20px;
`;

const WalletInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    width: 100%;
    gap: 15px;
  }
`;

const AccountDisplay = styled.div`
  background: rgba(20, 20, 30, 0.6);
  padding: 14px 22px;
  border-radius: 12px;
  font-family: 'Space Mono', monospace;
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  border: 1px solid rgba(148, 69, 255, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(148, 69, 255, 0.6);
    box-shadow: 0 4px 25px rgba(148, 69, 255, 0.2);
  }
  
  &::before {
    content: "WALLET";
    position: absolute;
    top: -8px;
    left: 10px;
    font-size: 10px;
    background-color: rgba(20, 20, 30, 0.8);
    padding: 0 6px;
    color: #9945FF;
    font-weight: bold;
    border-radius: 4px;
  }
  
  @media (max-width: 600px) {
    width: 100%;
    text-align: center;
  }
`;

const BalanceDisplay = styled.div`
  background: rgba(20, 20, 30, 0.6);
  padding: 14px 22px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #14F195;
  border: 1px solid rgba(20, 241, 149, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(20, 241, 149, 0.6);
    box-shadow: 0 4px 25px rgba(20, 241, 149, 0.2);
  }
  
  &::before {
    content: "BALANCE";
    position: absolute;
    top: -8px;
    left: 10px;
    font-size: 10px;
    background-color: rgba(20, 20, 30, 0.8);
    padding: 0 6px;
    color: #14F195;
    font-weight: bold;
    border-radius: 4px;
  }
  
  @media (max-width: 600px) {
    width: 100%;
    text-align: center;
  }
`;

// Custom styles for the WalletMultiButton
const StyledWalletMultiButton = styled(WalletMultiButton)`
  background: linear-gradient(45deg, #9945FF, #14F195) !important;
  transition: all 0.3s ease !important;
  border-radius: 12px !important;
  padding: 0 24px !important;
  height: 48px !important;
  font-family: 'Poppins', sans-serif !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  box-shadow: 0 8px 25px rgba(153, 69, 255, 0.3) !important;
  border: none !important;
  
  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 10px 30px rgba(153, 69, 255, 0.4) !important;
  }
  
  &:active {
    transform: translateY(1px) !important;
  }
  
  &:not([disabled]):hover {
    background: linear-gradient(45deg, #8935EF, #04E185) !important;
  }
  
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(20, 20, 30, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  transition: all 0.3s ease;
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const LoadingText = styled.div`
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 1px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top: 4px solid #14F195;
  border-left: 4px solid #9945FF;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const WalletConnect: React.FC = () => {
  const { account, isConnected, balance } = useWeb3();
  const { connecting } = useWallet();
  const [isLoading, setIsLoading] = useState(false);
  
  // Handle loading state when connecting
  useEffect(() => {
    setIsLoading(connecting);
  }, [connecting]);

  return (
    <>
      {isLoading && (
        <LoadingOverlay>
          <LoadingSpinnerContainer>
            <LoadingSpinner />
            <LoadingText>Connecting Wallet...</LoadingText>
          </LoadingSpinnerContainer>
        </LoadingOverlay>
      )}
      <WalletConnectContainer>
        {isConnected && (
          <WalletInfo>
            <AccountDisplay>{account}</AccountDisplay>
            <BalanceDisplay>{balance.toFixed(2)} SOL</BalanceDisplay>
          </WalletInfo>
        )}
        <StyledWalletMultiButton />
      </WalletConnectContainer>
    </>
  );
};

export default WalletConnect; 